/**
 * Passwordless sign-in via Appwrite Magic URL.
 *
 * There is no password anywhere in this app. Requesting a link mints a token,
 * the emailed link returns with `userId` and `secret` on the query string, and
 * trading those for a session is the whole of "logging in".
 */
import { ID } from 'appwrite';
import { account, messageOf } from './api';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
}

/** Outcome of inspecting the URL for magic-link parameters on page load. */
export type CallbackResult =
  | { kind: 'none' }
  | { kind: 'signed-in' }
  | { kind: 'failed'; message: string };

/**
 * Who is signed in, or null.
 *
 * `account.get()` throws for anonymous visitors. That is the normal signed-out
 * path, not an error worth showing anyone.
 */
export async function getCurrentUser(): Promise<CurrentUser | null> {
  try {
    const user = await account.get();
    return { id: user.$id, email: user.email, name: user.name };
  } catch {
    return null;
  }
}

/**
 * Email a one-time sign-in link that returns to this origin.
 *
 * `returnPath` is the in-app route the link should land on, so someone who
 * followed "review and sign your lease" straight into the sign-in wall comes
 * back to that document rather than to the inbox. It travels in the link
 * itself, which also survives opening the email on another device.
 */
export async function sendMagicLink(email: string, returnPath = '/'): Promise<void> {
  await account.createMagicURLToken({
    userId: ID.unique(),
    email,
    url: `${window.location.origin}${samePathOnly(returnPath)}`,
  });
}

/** Keep the return to this site: no other origin, no protocol-relative path. */
function samePathOnly(path: string): string {
  if (!path.startsWith('/') || path.startsWith('//')) return '/';
  return path;
}

/**
 * If this page load is a magic-link return, trade the token for a session.
 *
 * The secret is stripped from the address bar whatever happens, so it cannot be
 * bookmarked, pasted into a chat, or leaked through a referrer.
 */
export async function consumeMagicLinkCallback(): Promise<CallbackResult> {
  const params = new URLSearchParams(window.location.search);
  const userId = params.get('userId');
  const secret = params.get('secret');
  if (!userId || !secret) return { kind: 'none' };

  try {
    await account.createSession({ userId, secret });
    return { kind: 'signed-in' };
  } catch (err) {
    return {
      kind: 'failed',
      message: messageOf(
        err,
        'That sign-in link is no longer valid. Links expire after an hour - request a new one.',
      ),
    };
  } finally {
    stripAuthParams(params);
  }
}

/** Remove the one-time credentials from the URL without reloading the page. */
function stripAuthParams(params: URLSearchParams): void {
  params.delete('userId');
  params.delete('secret');
  const query = params.toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', url);
}

/** End the session on this device. */
export async function signOut(): Promise<void> {
  await account.deleteSession({ sessionId: 'current' });
}
